/**
 * Client-side voice fast tracks (V1-CLIENT-SAFETY).
 *
 * Safe only: navigate, change filter, refresh, focus search, show record, toggle view.
 * Expense / invoice / contact creation must go through the canonical server command flow.
 * No function in this module may POST accounting mutations.
 */

export interface FastTrackResult {
  handled: boolean;
  message?: string;
  navigationHref?: string;
  continueToServer?: boolean;
  action?: string;
}

type PageActionEvent =
  | 'voiceContactsAction'
  | 'voiceExpensesAction'
  | 'voiceInvoicesAction'
  | 'voiceQuotesAction'
  | 'voiceBankAccountsAction'
  | 'voiceInventoryAction'
  | 'voiceTaxPeriodsAction'
  | 'voiceReportsAction'
  | 'voiceBulkPaymentsAction'
  | 'voiceChartOfAccountsAction'
  | 'voiceDashboardAction'
  | 'voiceImageProcessingAction';

const PAGE_ACTION_BY_PATH: Array<{ prefix: string; event: PageActionEvent }> = [
  { prefix: '/contacts', event: 'voiceContactsAction' },
  { prefix: '/expenses', event: 'voiceExpensesAction' },
  { prefix: '/invoices', event: 'voiceInvoicesAction' },
  { prefix: '/sales-invoices', event: 'voiceInvoicesAction' },
  { prefix: '/purchase-invoices', event: 'voiceInvoicesAction' },
  { prefix: '/quotes', event: 'voiceQuotesAction' },
  { prefix: '/bank-accounts', event: 'voiceBankAccountsAction' },
  { prefix: '/inventory', event: 'voiceInventoryAction' },
  { prefix: '/tax-periods', event: 'voiceTaxPeriodsAction' },
  { prefix: '/reports', event: 'voiceReportsAction' },
  { prefix: '/bulk-payments', event: 'voiceBulkPaymentsAction' },
  { prefix: '/chart-of-accounts', event: 'voiceChartOfAccountsAction' },
  { prefix: '/dashboard', event: 'voiceDashboardAction' },
  { prefix: '/image-processing', event: 'voiceImageProcessingAction' },
];

const dispatchPageAction = (detail: Record<string, unknown>): PageActionEvent | null => {
  const pathname = window.location.pathname || '';
  const match = PAGE_ACTION_BY_PATH.find(({ prefix }) => pathname === prefix || pathname.startsWith(`${prefix}/`));
  if (!match) return null;
  window.dispatchEvent(new CustomEvent(match.event, { detail }));
  return match.event;
};

const pushClientRoute = (href: string): void => {
  if (window.history?.pushState) {
    window.history.pushState(null, '', href);
    window.dispatchEvent(new PopStateEvent('popstate'));
  } else {
    window.location.href = href;
  }
};

/**
 * @deprecated Mutation fast tracks are disabled (V1-CLIENT-SAFETY).
 * Always returns null so callers fall through to the server command path.
 */
export const tryCreateExpenseFastTrack = async (_text: string): Promise<FastTrackResult | null> => null;

/**
 * @deprecated Mutation fast tracks are disabled (V1-CLIENT-SAFETY).
 * Always returns null so callers fall through to the server command path.
 */
export const tryCreateInvoiceFastTrack = async (_text: string): Promise<FastTrackResult | null> => null;

/**
 * @deprecated Mutation fast tracks are disabled (V1-CLIENT-SAFETY).
 * Always returns null so callers fall through to the server command path.
 */
export const tryCreateContactFastTrack = async (_text: string): Promise<FastTrackResult | null> => null;

export const tryClientNavigationFastTrack = (text: string): FastTrackResult | null => {
  const navPatterns = [
    {
      pattern:
        /(?:go to|take me to|navigate to|open|show me)\s+(?:the\s+)?(payables|accounts\s+payable|purchase\s+invoices?|receivables|accounts\s+receivable|dashboard|contacts|invoices|quotes|quotations|quotation|expenses|reports|settings)/i,
      page: (match: RegExpMatchArray) => {
        const dest = match[1].toLowerCase();
        if (dest.includes('payable') || dest.includes('purchase')) return '/invoices?tab=ap';
        if (dest.includes('receivable')) return '/invoices?tab=ar';
        if (dest === 'quotations' || dest === 'quotation') return '/quotes';
        return `/${dest}`;
      },
    },
    {
      pattern: /^(dashboard|contacts|invoices|quotes|expenses|reports|settings)$/i,
      page: (match: RegExpMatchArray) => `/${match[1].toLowerCase()}`,
    },
  ];
  for (const { pattern, page } of navPatterns) {
    const match = text.match(pattern);
    if (match) {
      return {
        handled: true,
        message: `Navigating to ${match[1]}`,
        navigationHref: page(match),
        action: 'navigate',
      };
    }
  }
  return null;
};

export const tryChangeFilterFastTrack = (text: string): FastTrackResult | null => {
  const trimmed = text.trim();
  const customerMatch = trimmed.match(
    /(?:show|filter|filter\s+by|only)\s+(?:the\s+)?(customers?|clients?|buyers?)/i
  );
  const supplierMatch = trimmed.match(
    /(?:show|filter|filter\s+by|only)\s+(?:the\s+)?(suppliers?|vendors?|sellers?)/i
  );
  const statusMatch = trimmed.match(
    /(?:filter|show|only)\s+(?:by\s+)?(?:status\s+)?(draft|paid|unpaid|overdue|pending|approved|void)/i
  );
  const clearMatch = /^(?:clear|reset)\s+filters?$/i.test(trimmed);

  if (clearMatch) {
    const event = dispatchPageAction({ clearFilters: true });
    if (!event) return null;
    return { handled: true, message: 'Cleared filters.', action: 'change_filter' };
  }

  if (customerMatch) {
    const event = dispatchPageAction({ filterType: 'customer' });
    if (!event) {
      pushClientRoute('/contacts?type=customer');
      return { handled: true, message: 'Showing customers.', navigationHref: '/contacts?type=customer', action: 'change_filter' };
    }
    return { handled: true, message: 'Showing customers.', action: 'change_filter' };
  }

  if (supplierMatch) {
    const event = dispatchPageAction({ filterType: 'supplier' });
    if (!event) {
      pushClientRoute('/contacts?type=supplier');
      return { handled: true, message: 'Showing suppliers.', navigationHref: '/contacts?type=supplier', action: 'change_filter' };
    }
    return { handled: true, message: 'Showing suppliers.', action: 'change_filter' };
  }

  if (statusMatch) {
    const status = statusMatch[1].toLowerCase();
    const event = dispatchPageAction({
      status,
      filters: { status },
    });
    if (!event) return null;
    return { handled: true, message: `Filtered by ${status}.`, action: 'change_filter' };
  }

  return null;
};

export const tryRefreshFastTrack = (text: string): FastTrackResult | null => {
  if (!/^(?:refresh|reload)(?:\s+(?:the\s+)?(?:page|data|list|view))?$/i.test(text.trim())) {
    return null;
  }

  const event = dispatchPageAction({ refresh: true });
  window.dispatchEvent(
    new CustomEvent('voiceDataRefresh', {
      detail: { resources: ['all'], reason: 'client_fast_track_refresh' },
    })
  );

  return {
    handled: true,
    message: event ? 'Refreshing data.' : 'Refreshing.',
    action: 'refresh',
  };
};

export const tryFocusSearchFastTrack = (text: string): FastTrackResult | null => {
  if (!/(?:focus|open|go to|activate)\s+(?:the\s+)?search(?:\s+(?:box|field|bar|input))?/i.test(text.trim())
    && !/^search$/i.test(text.trim())) {
    return null;
  }

  dispatchPageAction({ focusSearch: true });

  const candidates = Array.from(
    document.querySelectorAll<HTMLElement>(
      'input[type="search"], input[placeholder*="Search" i], input[placeholder*="search" i], .search-box input, .page-filters input[type="text"]'
    )
  );
  const target = candidates.find((el) => {
    const style = window.getComputedStyle?.(el);
    return !style || (style.visibility !== 'hidden' && style.display !== 'none');
  });
  if (target && typeof target.focus === 'function') {
    target.focus();
  }

  return { handled: true, message: 'Search focused.', action: 'focus_search' };
};

export const tryShowRecordFastTrack = (text: string): FastTrackResult | null => {
  const patterns: Array<{ pattern: RegExp; href: (id: string) => string; label: string }> = [
    {
      pattern: /(?:show|open|view)\s+(?:sales\s+)?invoice\s+#?\s*(\d+)/i,
      href: (id) => `/sales-invoices/${id}`,
      label: 'invoice',
    },
    {
      pattern: /(?:show|open|view)\s+(?:purchase\s+)?(?:bill|invoice)\s+#?\s*(\d+)/i,
      href: (id) => `/purchase-invoices/${id}`,
      label: 'bill',
    },
    {
      pattern: /(?:show|open|view)\s+(?:quote|quotation)\s+#?\s*(\d+)/i,
      href: (id) => `/quotes/${id}`,
      label: 'quote',
    },
    {
      pattern: /(?:show|open|view)\s+(?:expense)\s+#?\s*(\d+)/i,
      href: (id) => `/expenses?view=${id}`,
      label: 'expense',
    },
    {
      pattern: /(?:show|open|view)\s+(?:contact|customer|supplier)\s+#?\s*(\d+)/i,
      href: (id) => `/contacts/${id}`,
      label: 'contact',
    },
  ];

  for (const { pattern, href, label } of patterns) {
    const match = text.match(pattern);
    if (!match) continue;
    const navigationHref = href(match[1]);
    return {
      handled: true,
      message: `Opening ${label} ${match[1]}.`,
      navigationHref,
      action: 'show_record',
    };
  }
  return null;
};

export const tryToggleViewFastTrack = (text: string): FastTrackResult | null => {
  const trimmed = text.trim();
  const grid = /(?:switch\s+to\s+)?grid\s+view|show\s+(?:as\s+)?grid|toggle\s+(?:to\s+)?grid/i.test(trimmed);
  const list = /(?:switch\s+to\s+)?list\s+view|show\s+(?:as\s+)?list|toggle\s+(?:to\s+)?list/i.test(trimmed);
  const toggle = /^toggle\s+view$/i.test(trimmed);

  if (!grid && !list && !toggle) return null;

  const viewMode = grid ? 'grid' : list ? 'list' : 'toggle';
  const event = dispatchPageAction({ viewMode });
  if (!event && viewMode !== 'toggle') {
    const url = new URL(window.location.href);
    url.searchParams.set('view', viewMode);
    pushClientRoute(`${url.pathname}${url.search}`);
    return {
      handled: true,
      message: `Switched to ${viewMode} view.`,
      navigationHref: `${url.pathname}${url.search}`,
      action: 'toggle_view',
    };
  }
  if (!event) return null;

  return {
    handled: true,
    message: viewMode === 'toggle' ? 'Toggled view.' : `Switched to ${viewMode} view.`,
    action: 'toggle_view',
  };
};

/** Run all safe (non-mutating) client fast tracks. */
export const trySafeClientFastTrack = (text: string): FastTrackResult | null => {
  const trimmed = text.trim();
  if (!trimmed) return null;

  return (
    tryClientNavigationFastTrack(trimmed)
    || tryShowRecordFastTrack(trimmed)
    || tryChangeFilterFastTrack(trimmed)
    || tryRefreshFastTrack(trimmed)
    || tryFocusSearchFastTrack(trimmed)
    || tryToggleViewFastTrack(trimmed)
  );
};
